import type { ReactElement } from 'react';
import type { GetServerSideProps, InferGetServerSidePropsType, Redirect } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getStoreSlugData } from 'services/getStoreSlugData';
import { StoreLocatorDetail } from '@storefront/lib-content';
import { Locale, MasterLayout, Meta, PageType, getStoreLocatorLink } from '@storefront/lib-global';
import { CmsSlotsProvider } from '@storefront/lib-global/context/cmsSlotsContext';
import { CmsSlot } from '@storefront/lib-global/globalComponents/cmsSlot';
import { getCmsSlotsDataForApplicationPages } from '@storefront/lib-global/services/amplience/getCmsSlotsData';
import { CMS_SLOTS_DELIVERY_KEYS } from '@storefront/lib-global/utils/amplience/constants';
import { setCustomCacheHeader } from '@storefront/lib-global/utils/setCustomCacheHeader';

export default function StoreSlugPage({
  storeData,
  storeLocatorLayout,
  breadcrumbData,
  storeUrl,
  cmsSlotsData,
}: InferGetServerSidePropsType<typeof getServerSideProps>): ReactElement {
  const { displayName, address, openingHours, _geoloc, features } = storeData[0] || {};

  return (
    <>
      <Meta
        metaTitle={`${displayName} | ${address?.town?.toLocaleUpperCase()}`}
        pageType={PageType.LOCATIONS}
        store={{ storeData: { displayName, address, openingHours, _geoloc, features }, storeUrl }}
      />
      <CmsSlotsProvider cmsSlotsData={cmsSlotsData}>
        <MasterLayout>
          <CmsSlot deliveryKey={CMS_SLOTS_DELIVERY_KEYS.STORE_LOCATOR_TOP_BANNER} />
          <StoreLocatorDetail
            storeData={storeData}
            storeLocatorLayout={storeLocatorLayout}
            breadcrumbData={breadcrumbData}
          />
        </MasterLayout>
      </CmsSlotsProvider>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ query, locale, res, req }) => {
  const { props, notFound, redirect } = await getStoreSlugData(query.storeSlug as string[] | undefined, locale, true);
  const { host, referer } = req.headers;
  const protocol = referer?.split('://')[0] || 'https';
  const storeData = props?.storeData[0];
  let storeUrl = '';

  if (storeData) {
    const { address, objectID } = storeData;
    storeUrl = `${protocol}://${host}/${locale}/${getStoreLocatorLink(address, objectID, false, locale)}`;
  }

  if (notFound) {
    return {
      notFound,
    };
  }

  if (redirect) {
    return {
      redirect: redirect as Redirect,
    };
  }

  const cmsSlotsProps = {
    locale: locale || Locale.DEFAULT_LOCALE,
    slug: ['store-locator'],
    deliveryKeys: [CMS_SLOTS_DELIVERY_KEYS.STORE_LOCATOR_TOP_BANNER],
  };
  const cmsSlotsData = await getCmsSlotsDataForApplicationPages(cmsSlotsProps);

  // cache for 10 minutes, and show expired cache for maximum of two minutes
  setCustomCacheHeader(res, { maxAge: 600, staleWhileRevalidate: 120 });

  return {
    props: {
      ...(props ?? {}),
      ...(await serverSideTranslations(locale || 'en', ['lib-global-common', 'lib-global-countries-common'])),
      locale,
      storeUrl,
      cmsSlotsData,
    },
  };
};
